import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  InputAdornment
} from '@mui/material';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import profileApi from '../../../app/api/profileApi';
import TextInputComponent from '../../../components/input/TextInputComponent';
import EventDetailsDialog from '../../reports/sections/event-details-dialog';

const ProfileEventsTab = ({ auth, siteId, accountId, profileId }) => {
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]); // Store all fetched events
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsSearch, setEventsSearch] = useState('');
  const [payloadDialogOpen, setPayloadDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleViewPayload = (event) => {
    setSelectedEvent(event);
    setPayloadDialogOpen(true);
  };

  const handleClosePayloadDialog = () => {
    setPayloadDialogOpen(false);
    setSelectedEvent(null);
  };

  useEffect(() => {
    if (allEvents.length === 0) {
      setLoadingEvents(true);
      profileApi.getProfileEvents(auth.user.access_token, siteId, accountId, profileId)
        .then(r => {
          setAllEvents(r.data);
          setEvents(r.data);
        })
        .catch(err => console.error(err))
        .finally(() => setLoadingEvents(false));
    }
  }, [auth.user.access_token, siteId, accountId, profileId, allEvents.length]);

  useEffect(() => {
    // Filter events locally by payload search
    if (allEvents.length > 0) {
      if (!eventsSearch) {
        setEvents(allEvents);
      } else {
        const terms = eventsSearch.toLowerCase().split(' ').filter(term => term.length > 0);
        const filtered = allEvents.filter(event => {
          const payload = (event.payload || '').toLowerCase();
          return terms.every(term => payload.includes(term));
        });
        setEvents(filtered);
      }
    } else {
      setEvents([]);
    }
  }, [allEvents, eventsSearch]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <TextInputComponent
          value={eventsSearch}
          setValue={setEventsSearch}
          label="Search in Payload"
          placeholder="Type words separated by space to filter..."
          inputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="ri:search-line" color="gray" width="24" height="24" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loadingEvents ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="events table">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Payload</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {dayjs(row.eventTimeStamp).format('DD/MM/YYYY HH:mm:ss')}
                  </TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.message}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={row.payload || ''}>
                      <span>{row.payload}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {row.payload && (
                      <Tooltip title="View payload">
                        <IconButton size="small" onClick={() => handleViewPayload(row)}>
                          <Icon icon="mdi:code-json" width="20" height="20" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No events found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EventDetailsDialog
        dialogOpen={payloadDialogOpen}
        eventDetails={selectedEvent}
        okAction={handleClosePayloadDialog}
      />
    </Box>
  );
};

export default ProfileEventsTab;

